// App.js

import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Navbar from "./components/Navbar";

// Auth provider/context
import { AuthProvider, useAuth } from "./admin/AuthProvider";

// Public pages
import Home from "./pages/Home";
import About from "./pages/About";
import HandmadeGifts from "./pages/HandmadeGifts";
import Achievements from "./pages/Achievements";
import Testimonials from "./pages/Testimonials";
import Services from "./pages/Services";
import Portfolio from "./pages/Portfolio";
import Contact from "./pages/Contact";

// Admin (lazy)
const AdminLayout = lazy(() => import("./admin/AdminLayout"));
const AdminLogin = lazy(() => import("./admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));
const AdminHome = lazy(() => import("./admin/AdminHome"));
const AdminProjects = lazy(() => import("./admin/AdminProjects"));
const AdminFeatured = lazy(() => import("./admin/AdminFeatured"));
const AdminSettings = lazy(() => import("./admin/AdminSettings"));
const AdminServices = lazy(() => import("./admin/AdminServices"));
const AdminContact = lazy(() => import("./admin/AdminContact"));
const AdminPortfolio = lazy(() => import("./admin/AdminPortfolio"));
const AdminAbout = lazy(() => import("./admin/AdminAbout"));
const AdminHandmade = lazy(() => import("./admin/AdminHandmade"));
// ✅ NEW
const AdminAchievements = lazy(() => import("./admin/AdminAchievements"));
const AdminTestimonials = lazy(() => import("./admin/AdminTestimonials"));

function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppInner() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      {!isAdminRoute && <Navbar />}

      <Suspense fallback={null}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/handmade-gifts" element={<HandmadeGifts />} />
          <Route path="/achievements" element={<Achievements />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/services" element={<Services />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/contact" element={<Contact />} />

          {/* Admin auth */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Admin app (protected) */}
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="home" element={<AdminHome />} />
            <Route path="featured" element={<AdminFeatured />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="services" element={<AdminServices />} />
            <Route path="portfolio" element={<AdminPortfolio />} />
            <Route path="about" element={<AdminAbout />} />
            <Route path="handmade" element={<AdminHandmade />} />
            <Route path="achievements" element={<AdminAchievements />} />
            {/* ✅ NEW */}
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="contact" element={<AdminContact />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </Router>
  );
}
